import React, { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import PageLayout from './PageLayout';
import AuthService from '../services/AuthService';
import ClientService from '../services/ClientService';
import { useLicenseKey } from '../hooks/useLicenseDetails';
import { T, useT } from '../Translation/index';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  background: #f8f9fa;
  padding: 24px 16px;
  @media (max-width: 679px) {
    padding: 16px 12px;
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
  border: 1px solid #e8ecf1;
  padding: 36px 28px;
  width: 100%;
  max-width: 380px;
  @media (max-width: 679px) {
    padding: 28px 20px;
    border-radius: 14px;
  }
`;

const LogoArea = styled.div`
  text-align: center;
  margin-bottom: 28px;
`;

const LogoIcon = styled.img`
  height: 72px;
  margin: 0 auto 20px;
  display: block;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: #191c1d;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: #6c757d;
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #495057;
`;

const Input = styled.input`
  padding: 11px 14px;
  border: 1px solid #dde1e6;
  border-radius: 10px;
  font-size: 15px;
  color: #191c1d;
  background: #fafbfc;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: #1976d2;
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
    background: #fff;
  }

  &::placeholder {
    color: #adb5bd;
  }
`;

const Button = styled.button`
  margin-top: 6px;
  padding: 13px;
  background: #1976d2;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;

  &:hover {
    background: #1565c0;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ErrorMessage = styled.div`
  color: #c62828;
  font-size: 13px;
  text-align: center;
  padding: 10px;
  background: #ffebee;
  border-radius: 8px;
`;

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [, setLicenseKeyInLocalStorage] = useLicenseKey();
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const t = useT();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const authService = AuthService.getInstance();
    const clientService = ClientService.getInstance();
    await authService.getAuthorization();
    try {
      const msg1 = await clientService.handleLogin(username, password);
      if (!msg1) {
        const msg2 = await clientService.handleLicence();
        if (!msg2 || msg2.length < 42) {
          setError(msg2 || 'Licence fetch failed');
        } else {
          setLicenseKeyInLocalStorage(msg2);
          navigate('/monitor');
        }
      } else {
        setError(msg1);
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 400) {
          setError('UserName/Email or password is wrong.');
        } else {
          setError(error.response.data.msg || '登入失敗');
        }
      } else {
        setError('UserName/Email or password is wrong.');
      }
    }
  };

  return (
    <PageLayout>
      <Container>
        <Card>
          <LogoArea>
            <LogoIcon src={require('../assets/charlycam.png')} alt="App Icon" />
            <Title><T id="login_title" /></Title>
            <Subtitle><T id="login_subtitle" /></Subtitle>
          </LogoArea>

          <Form onSubmit={handleSubmit}>
            <Field>
              <Label><T id="username_label" /></Label>
              <Input
                type="text"
                placeholder={t('username_placeholder')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </Field>
            <Field>
              <Label><T id="password_label" /></Label>
              <Input
                type="password"
                placeholder={t('password_placeholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Field>
            <Button type="submit"><T id="sign_in" /></Button>
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </Form>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default LoginPage;
